import CreationImage from "$/components/CreationImage";
import CreditProgress from "$/components/CreditProgress";
import ExpiredOverlay from "$/components/ExpiredOverlay";
import ProBadge from "$/components/icons/ProBadge";
import InkProgress from "$/components/InkProgress";
import PricingModal from "$/components/PricingModal";
import SocialShareModal from "$/components/SocialShareModal";
import UpgradeBanner from "$/components/UpgradeBanner";
import commonConstants from "$/data/common";
import { getTextStyleOptions, TEXT_STYLES } from "$/data/textStyles";
import analytics from "$/util/analytics";
import { useAuth } from "$/util/auth";
import { gradientGrainStyling } from "$/util/background";
import { createCreation, refreshUser, subscribeCreation } from "$/util/db";
import { createTextMask } from "$/util/mask";
import { getCreationUrl } from "@blackink/core/creation/util";
import CreateIcon from "@mui/icons-material/DesignServices";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrowRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import supabase from "../../util/supabase";

export default function TextDesignTool() {
  const auth = useAuth();
  const theme = useTheme();
  const [pending, setPending] = useState(false);
  const [creation, setCreation] = useState(null);
  const [variant, setVariant] = useState(0);
  const [formAlert, setFormAlert] = useState(null);
  const [openShareModal, setOpenShareModal] = useState(false);
  // const [maskPreview, setMaskPreview] = useState<HTMLCanvasElement | null>(null);
  const [formValues, setFormValues] = useState({
    text: "",
    style: "default",
    public: true,
    showAllStyles: false,
  });
  const [textError, setTextError] = useState("");
  const previewRef = useRef(null);

  // useEffect(() => {
  //   if (formValues.text) {
  //     const { canvas } = createTextMask(formValues.text, formValues.style, {
  //       width: 768,
  //       height: 341,
  //     });
  //     setMaskPreview(canvas);
  //   } else {
  //     setMaskPreview(null);
  //   }
  // }, [formValues.text, formValues.style]);

  const creditsDepleted = auth.user && auth.user.credits <= 0 && !auth.isProUser;
  const anonCreditsDepleted =
    auth.isAnonymousUser && auth.user && auth.user.credits <= 0;
  const creationReady = creation && creation.status === "SUCCESS";

  const handleCreationEvent = (creationEvent) => {
    setCreation(creationEvent.new);
    if (
      creationEvent.new.status === "SUCCESS" ||
      creationEvent.new.status === "ERROR"
    ) {
      setPending(false);
      auth.user && refreshUser(auth.user.id);
      supabase.removeAllChannels();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValues.text.trim()) {
      setTextError("Please enter some text for your tattoo design");
      return;
    }
    setTextError("");

    if (!auth.user) {
      // Join anonymously, passthrough user because hooks won't have updated user yet
      await auth.signinAnonymously();
      return; // Return and let the useEffect handle the creation after auth is updated
    }

    setPending(true);

    analytics.track("create_text", {
      style: formValues.style,
      public: formValues.public,
    });

    try {
      // Deep copy form values to avoid mutations
      const requestData = JSON.parse(JSON.stringify(formValues));

      const { rleMask } = createTextMask(requestData.text, requestData.style, {
        width: 512,
        height: 341,
      });

      const creation = await createCreation({
        owner: auth.user?.id,
        prompt: `"${requestData.text}"`,
        style: [TEXT_STYLES[requestData.style].style],
        modifiers: [],
        bodyPart: "font",
        public: requestData.public,
        params: {
          text_content: requestData.text,
          template: rleMask,
          strength: 0.05,
          num_inference_steps: 20,
          width: 512,
          height: 341,
        },
      });

      console.log("Creation response:", creation);

      setCreation(creation);
      subscribeCreation(creation.id, handleCreationEvent);

      // Scroll to the preview area
      previewRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (error) {
      console.error("Error creating text design:", {
        error,
        formValues,
        user: {
          id: auth.user?.id,
          isAnonymous: auth.isAnonymousUser,
          credits: auth.user?.credits,
        },
      });
      setPending(false);
      setFormAlert({
        type: "error",
        message: error.message || "Failed to create design. Please try again.",
      });
    }
  };

  return (
    <Box flexGrow={1} mb={2} mt={1}>
      {formAlert && (
        <Box mb={4}>
          <Alert severity={formAlert.type}>{formAlert.message}</Alert>
        </Box>
      )}
      {creditsDepleted && auth.user && (auth.user.userType || auth.isAnonymousUser) && (
        <PricingModal />
      )}

      <Grid container spacing={2} rowSpacing={0}>
        <Grid item xs={12} md={5}>
          <Typography variant="h5" gutterBottom>
            AI Tattoo Font Generator
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph mb={1}>
            Create custom tattoo lettering designs with various styles including
            gangster, cursive, gothic, and more using our AI-powered font generator. See
            how your quote, name, or phrase can be a unique tattoo design.
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Want design elements with text?
            </Typography>
            <Button
              component={Link}
              to="/tattoo-design-generator"
              variant="text"
              color="inherit"
              size="small"
              startIcon={<CreateIcon fontSize="small" />}
              sx={{ minWidth: 150 }}
            >
              Use AI Designer
            </Button>
          </Stack>

          {!auth.isProUser && (
            <CreditProgress
              credits={auth.user ? auth.user.credits : commonConstants.FREE_CREDITS}
            />
          )}

          <Box position="relative" pt={1}>
            {(creditsDepleted || anonCreditsDepleted) && <ExpiredOverlay />}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Your Text"
                  placeholder="Enter the text for your tattoo"
                  fullWidth
                  value={formValues.text}
                  onChange={(e) => {
                    const newText = e.target.value;
                    const lines = newText.split("\n");
                    if (lines.length > 2) {
                      // Only keep first two lines
                      const limitedText = lines.slice(0, 2).join("\n");
                      if (limitedText.length <= 30) {
                        setFormValues({ ...formValues, text: limitedText });
                        setTextError("");
                      }
                    } else if (newText.length <= 30) {
                      setFormValues({ ...formValues, text: newText });
                      setTextError("");
                    }
                  }}
                  multiline
                  minRows={2}
                  maxRows={2}
                  disabled={creditsDepleted || anonCreditsDepleted}
                  helperText={
                    textError || (
                      <>
                        {formValues.text.length}/30 characters (max 2 lines) <br />*
                        Results might be misspelled due to AI interpretation
                      </>
                    )
                  }
                  error={!!textError}
                />

                <Typography variant="subtitle2" gutterBottom>
                  Choose a Style
                </Typography>
                <Box>
                  <Grid container spacing={1}>
                    {getTextStyleOptions()
                      .slice(0, formValues.showAllStyles ? undefined : 6)
                      .map(({ value, label }) => (
                        <Grid item xs={6} key={value}>
                          <Button
                            fullWidth
                            variant={
                              formValues.style === value ? "contained" : "outlined"
                            }
                            onClick={() =>
                              setFormValues({ ...formValues, style: value })
                            }
                            sx={{ textTransform: "none" }}
                            disabled={creditsDepleted || anonCreditsDepleted}
                            color={formValues.style === value ? "primary" : "inherit"}
                          >
                            {label}
                          </Button>
                        </Grid>
                      ))}
                  </Grid>
                  {getTextStyleOptions().length > 6 && (
                    <Button
                      fullWidth
                      onClick={() =>
                        setFormValues({
                          ...formValues,
                          showAllStyles: !formValues.showAllStyles,
                        })
                      }
                      sx={{ mt: 1, textTransform: "none" }}
                      endIcon={
                        formValues.showAllStyles ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      }
                      color="inherit"
                    >
                      {formValues.showAllStyles
                        ? "Show Less Styles"
                        : `Show ${getTextStyleOptions().length - 6} More Styles`}
                    </Button>
                  )}
                </Box>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle3" textAlign="right">
                      <VisibilityIcon sx={{ mb: -0.8 }} fontSize="medium" /> Keep
                      Private?
                    </Typography>
                    {!auth.isProUser && (
                      <Chip
                        label={
                          <>
                            <ProBadge
                              color={theme.palette.primary.contrastText}
                              height={16}
                              sx={{ ml: -0.5, mb: -0.4 }}
                            />
                            Pro Only
                          </>
                        }
                        color="primary"
                        size="small"
                        sx={{ mx: 1 }}
                      />
                    )}
                  </Stack>
                  <Switch
                    checked={!formValues.public}
                    disabled={!auth.isProUser}
                    onChange={(e) =>
                      setFormValues({ ...formValues, public: !e.target.checked })
                    }
                  />
                </Stack>

                {/* {maskPreview && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <Box
                      component="img"
                      src={maskPreview.toDataURL()}
                      alt="Text Mask Preview"
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        border: "1px solid #eee",
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                )} */}

                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  loading={pending || (creation && creation.status === "IN_PROGRESS")}
                  disabled={creditsDepleted || anonCreditsDepleted}
                  loadingPosition="start"
                  startIcon={<DoubleArrowIcon />}
                  sx={{
                    mt: 3,
                    ...gradientGrainStyling,
                  }}
                  fullWidth
                >
                  Create
                </LoadingButton>
              </Stack>
            </form>
          </Box>

          <Box mt={3} sx={{ display: { xs: "none", md: "block" } }}>
            <UpgradeBanner context="create" />
          </Box>
        </Grid>

        <Grid item xs={12} md={7} ref={previewRef}>
          {creationReady ? (
            <Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ position: "relative" }}
              >
                <Button endIcon={<ShareIcon />} onClick={() => setOpenShareModal(true)}>
                  Share
                </Button>
                <SocialShareModal
                  open={openShareModal}
                  onClose={() => setOpenShareModal(false)}
                  creation={creation}
                  variant={variant}
                  mediaUrl={creation.imagePaths[0]}
                  linkUrl={`${window.location.protocol}//${
                    window.location.host
                  }${getCreationUrl(creation)}`}
                />
              </Box>
              <Stack spacing={2} sx={{ py: 2 }}>
                {[0, 1].map((index) => (
                  <CreationImage
                    key={index}
                    creation={creation}
                    indexToShow={index}
                    responsive
                    showFavoriteOverlay
                    showDownloadOverlay
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ position: "relative" }}
              >
                <Button endIcon={<ShareIcon />} disabled>
                  Share
                </Button>
              </Box>
              <Stack spacing={2} sx={{ py: 2 }}>
                {[0, 1].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#f9f9f9",
                      border: "1px dashed #ccc",
                      borderRadius: 2,
                    }}
                  >
                    {pending || (creation && creation.status === "IN_PROGRESS") ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          opacity: 0.7,
                          maxWidth: 512,
                          width: "100%",
                          aspectRatio: "512/341",
                        }}
                      >
                        <InkProgress size={250} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          maxWidth: 512,
                          width: "100%",
                          aspectRatio: "512/341",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={
                            index === 0
                              ? "/images/styles/font2.webp"
                              : "/images/styles/font.webp"
                          }
                          alt="Example Text Tattoo Design"
                          style={{
                            objectFit: "contain",
                            opacity: 0.2,
                            filter: "grayscale(100%)",
                            width: "100%",
                            maxWidth: 512,
                            maxHeight: "100%",
                            aspectRatio: "512/341",
                          }}
                          draggable={false}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Box my={3} sx={{ display: { xs: "block", md: "none" } }}>
            <UpgradeBanner context="create" />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
